import React, { useState, useEffect, useCallback, memo } from 'react';
import { useKeyboard } from '../context/KeyboardContext';
import { Box, Button, Container, Typography, CircularProgress, Grid, Card, CardMedia, CardContent, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import NewsletterDialog from './newsletter/NewsletterDialog';
import CloseIcon from '@mui/icons-material/Close';
import { useCart } from '../contexts/CartContext';
import { useSession } from '../contexts/SessionContext';
import KioskIdError from './KioskIdError';

// Image from public assets folder
const headerImage = '/assets/images/header_banner3.png';

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  padding: theme.spacing(2),
  fontSize: '1.2rem',
  width: '100%',
  maxWidth: '400px',
  '&:first-of-type': {
    marginTop: theme.spacing(4),
  },
}));

// Subtle Ken Burns effect for card images
const kenBurns = keyframes`
  0% { transform: scale(1.05) translateY(0); }
  100% { transform: scale(1.12) translateY(-6px); }
`;

const LandingPage = memo(({ onDonateClick }) => {
  const kb = useKeyboard();
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setCart } = useCart();
  const { resetSession, session } = useSession();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [zelleOpen, setZelleOpen] = useState(false);

  // Auto-close Zelle dialog after 1 minute when opened
  useEffect(() => {
    if (!zelleOpen) return;
    const t = setTimeout(() => {
      setZelleOpen(false);
    }, 60000);
    return () => clearTimeout(t);
  }, [zelleOpen]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewImage(null);
  }, []);

  // Memoize the button click handler
  const handleButtonClick = useCallback((action) => {
    try {
      console.log(`${action} button clicked`);
      if (action === 'Pay/Donate' || action === 'General Donation') {
        if (typeof onDonateClick === 'function') {
          onDonateClick();
        } else {
          console.error('onDonateClick is not a function');
          setError('Navigation failed. Please try again.');
        }
      } else if (action === 'Newsletter Signup') {
        console.log('Newsletter signup clicked');
      }
    } catch (err) {
      console.error('Error in button click:', err);
      setError('An error occurred. Please try again.');
    }
  }, [onDonateClick]);

  // Use a single useEffect for all side effects
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      try {
        // Add a small delay to prevent flash of loading state
        await new Promise(resolve => setTimeout(resolve, 300));
        
        if (mounted) {
          // Proactively close any virtual keyboard and blur focus when Home loads
          try { kb?.close && kb.close(); } catch {}
          try { if (document && document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch {}
          // Clear cart and reset session on Home to start fresh for each user
          try {
            setCart([]);
            resetSession({ skipNavigation: true });
          } catch (e) {
            console.warn('Failed to reset session/cart on home:', e);
          }
          if (typeof onDonateClick !== 'function') {
            console.warn('onDonateClick is not a function');
          }
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Initialization error:', err);
        if (mounted) {
          setError('Failed to initialize page');
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, [onDonateClick, resetSession, setCart]);

  if (isLoading) {
    return (
      <Box sx={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: 'background.paper',
        zIndex: 9999
      }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ textAlign: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <>
      <KioskIdError kioskId={session?.kioskId} currentUrl={typeof window !== 'undefined' ? window.location.href : ''} />
      <Box sx={{ width: '100%', minHeight: '100vh', pt: '50px', pb: '50px', boxSizing: 'border-box', background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 50%, #FFF8E1 100%)' }}>
      <Box sx={{ maxWidth: 1700, mx: 'auto', px: { xs: 2, sm: 4 }, pt: { xs: 1, sm: 2 }, pb: { xs: 2, sm: 3 }, bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
      {/* Full-width Header Image */}
      <Box 
        component="img"
        src={headerImage}
        alt="ICCT Kiosk"
        sx={{
          width: '80%',
          maxHeight: '300px',
          objectFit: 'cover',
          display: 'block',
          marginBottom: 4,
          mx: 'auto',
          borderRadius: 3
        }}
      />
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '50vh',
            padding: 3,
            textAlign: 'center',
            marginTop: -4
          }}
        >

        <Typography variant="h3" component="h1" gutterBottom sx={{ mt: 2, mb: 0.5, fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '2rem', sm: '2.4rem' } }}>
          Welcome to the Self‑Service Kiosk
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#e65100', textAlign: 'center', mb: 1 }}>
          Pay with Zelle, credit or debit card.
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Donations, service payments & sponsorship
        </Typography>
        
        <Grid 
          container 
          spacing={3} 
          sx={{ 
            width: '100%', 
            maxWidth: 1400, 
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            '& > .MuiGrid-item': { maxWidth: '100%' }
          }}
        >
          <Grid item xs={12} sx={{ width: '100%' }}>
            <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
              <CardMedia
                component="img"
                height="280"
                image="/assets/images/temple.jpeg"
                alt="Temple"
                sx={{ 
                  objectFit: 'cover', 
                  animation: `${kenBurns} 18s ease-in-out infinite alternate`,
                  willChange: 'transform'
                }}
              />
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                background: 'linear-gradient(180deg, #f8f5ff 0%, #f3e5f5 100%)',
                borderTop: '1px solid rgba(0,0,0,0.06)'
              }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                  Temple – Donate, Services & Sponsorship
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Donations, pay for services (e.g. abhishekam, prasadam), or sponsor.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 1.5, py: 1.8, fontWeight: 800, letterSpacing: 0.3, borderRadius: 2, fontSize: '1.25rem' }}
                  onClick={() => (window.location.href = '/shop')}
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%' }}>
            <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
              <CardMedia
                component="img"
                height="280"
                image="/assets/images/kotilinga.jpeg"
                alt="Kotilinga Kshetram"
                sx={{ 
                  objectFit: 'cover', 
                  animation: `${kenBurns} 18s ease-in-out infinite alternate`,
                  willChange: 'transform'
                }}
              />
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                background: 'linear-gradient(180deg, #f8f5ff 0%, #f3e5f5 100%)',
                borderTop: '1px solid rgba(0,0,0,0.06)'
              }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                  Kotilinga Kshetram – Donate or Sponsor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Donate or sponsor the Kotilinga Kshetram initiative.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 1.5, py: 1.8, fontWeight: 800, letterSpacing: 0.3, borderRadius: 2, fontSize: '1.25rem' }}
                  onClick={() => (window.location.href = '/kotilinga')}
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <NewsletterDialog 
          open={newsletterOpen} 
          onClose={() => setNewsletterOpen(false)} 
        />

        {/* ICCT Logo separator (temporarily disabled) */}
        {false && (
          <Box
            component="img"
            src="/assets/images/ICCT-Logo-trans.webp"
            alt="ICCT Logo"
            sx={{
              display: 'block',
              mx: 'auto',
              mt: 8,
              mb: 5,
              width: { xs: 180, sm: 220 },
              height: 'auto',
              opacity: 0.95
            }}
          />
        )}

        {/* Sanskrit motto removed as requested */}

        {/* Social Media QR Codes Section */}
        <Box sx={{ 
          mt: 3, 
          mb: 1, 
          width: '100%',
          maxWidth: '1700px',
          mx: 'auto',
          backgroundColor: 'rgba(245, 245, 245, 0.7)',
          borderRadius: 2,
          p: 5, // +25%
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom 
            fontWeight="bold"
            sx={{ 
              mb: 2.5,
              color: 'primary.main',
              textTransform: 'uppercase',
              letterSpacing: 1,
              position: 'relative',
              fontSize: { xs: '2rem', sm: '2.25rem' }, // ~+25%
              '&:after': {
                content: '""',
                display: 'block',
                width: '100px', // +25%
                height: '5px', // +25%
                background: 'linear-gradient(90deg, #3f51b5, #9c27b0)',
                margin: '14px auto 0',
                borderRadius: '2px'
              }
            }}
          >
            Connect With Us
          </Typography>
          
          {/* Newsletter Signup button placed above the subtitle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
            <StyledButton
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => setNewsletterOpen(true)}
              sx={{
                // Override default StyledButton margins to tighten spacing
                my: '8px !important',
                mt: '8px !important',
                borderWidth: 2,
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.06)',
                color: 'primary.main',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: 'rgba(25, 118, 210, 0.12)'
                }
              }}
            >
              Signup for Newsletter
            </StyledButton>
          </Box>
          
          {/* Subtitle hidden per request */}
          
          <Grid container spacing={4} justifyContent="space-between" sx={{ 
            mt: 1,
            width: '100%',
            '& .MuiGrid-item': {
              flex: '0 0 auto',
              width: 'auto',
              maxWidth: 'none'
            },
            flexWrap: 'nowrap'
          }}>
            {[
              { 
                icon: <WhatsAppIcon color="success" sx={{ fontSize: 50 }} />, 
                name: 'WhatsApp', 
                qr: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(process.env.REACT_APP_WHATSAPP_URL || 'https://wa.me/yourwhatsapp')}`,
                url: process.env.REACT_APP_WHATSAPP_URL || 'https://wa.me/yourwhatsapp'
              },
              { 
                icon: <FacebookIcon color="primary" sx={{ fontSize: 50 }} />, 
                name: 'Facebook', 
                qr: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(process.env.REACT_APP_FACEBOOK_URL || 'https://facebook.com/yourpage')}`,
                url: process.env.REACT_APP_FACEBOOK_URL || 'https://facebook.com/yourpage'
              },
              { 
                icon: <InstagramIcon color="secondary" sx={{ fontSize: 50 }} />, 
                name: 'Instagram', 
                qr: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(process.env.REACT_APP_INSTAGRAM_URL || 'https://instagram.com/yourprofile')}`,
                url: process.env.REACT_APP_INSTAGRAM_URL || 'https://instagram.com/yourprofile'
              },
              { 
                icon: <TwitterIcon sx={{ color: '#1DA1F2', fontSize: 50 }} />, 
                name: 'Twitter', 
                qr: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(process.env.REACT_APP_TWITTER_URL || 'https://twitter.com/yourhandle')}`,
                url: process.env.REACT_APP_TWITTER_URL || 'https://twitter.com/yourhandle'
              },
              { 
                icon: <YouTubeIcon color="error" sx={{ fontSize: 50 }} />, 
                name: 'YouTube', 
                qr: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(process.env.REACT_APP_YOUTUBE_URL || 'https://youtube.com/yourchannel')}`,
                url: process.env.REACT_APP_YOUTUBE_URL || 'https://youtube.com/yourchannel'
              },
            ].map((social) => (
              <Grid item key={social.name}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    pt: 2,
                    px: 2,
                    pb: 1,
                    border: 'none',
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease-in-out',
                    height: '100%',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      mb: 1.5,
                      width: '62px',
                      height: '62px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(63, 81, 181, 0.1)',
                      cursor: 'default',
                      pointerEvents: 'none'
                    }}
                    aria-label={`${social.name} icon (disabled)`}
                  >
                    {social.icon}
                  </Box>
                  <Box 
                    component="img" 
                    src={social.qr} 
                    alt={`${social.name} QR Code`} 
                    sx={{ 
                      width: '112px', 
                      height: '112px',
                      objectFit: 'contain',
                      borderRadius: 2,
                      border: '1px solid #f0f0f0',
                      p: 1,
                      backgroundColor: 'white',
                      mt: 0.5,
                      mb: 0
                    }} 
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
          {/* Image Preview Dialog (moved inside content) */}
          <Dialog open={previewOpen} onClose={closePreview} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
              Upcoming Event
              <IconButton onClick={closePreview} sx={{ position: 'absolute', right: 8, top: 8 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
              {previewImage && (
                <Box
                  component="img"
                  src={previewImage.url}
                  alt={previewImage.title || 'Preview'}
                  sx={{
                    maxWidth: '90vw',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    transform: 'rotate(-90deg)',
                    transformOrigin: 'center center'
                  }}
                  onError={(e) => console.error('Error loading preview:', e)}
                />
              )}
            </DialogContent>
          </Dialog>
          {/* Close Social Media section Box */}
          </Box>
          {/* Close inner content Box */}
          </Box>
        </Container>
      </Box>
    </Box>
    </>
  );
});

export default LandingPage;
